// src/features/sheets/components/SpreadsheetToolbar.tsx
// Toolbar component for spreadsheet actions

import React from "react";
import {
  RefreshCw,
  HelpCircle,
  Loader2,
  Plus,
  Trash2,
  AlignLeft,
  AlignCenter,
  AlignRight,
  DollarSign,
  Percent,
  Hash,
  Copy,
  ClipboardPaste,
  Pencil,
} from "lucide-react";
import type { CellPosition, CellFormat } from "../types";

// ─────────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────────

export interface SpreadsheetToolbarProps {
  sheetName?: string | undefined;
  selectedCell: CellPosition | null;
  currentFormat?: CellFormat | undefined;
  onFormatChange: (format: Partial<CellFormat>) => void;
  onAddRow: () => void;
  onDeleteRow: () => void;
  onAddColumn: () => void;
  onDeleteColumn: () => void;
  onCopy: () => void;
  onPaste: () => void;
  liveCellCount: number;
  onRefreshLive: () => void;
  isLoadingLive: boolean;
  canDeleteRow: boolean;
  canDeleteColumn: boolean;
  showHelp?: boolean | undefined;
  onToggleHelp?: (() => void) | undefined;
  readOnly?: boolean | undefined;
  /** When true, renders only the toolbar controls without the outer container - useful for embedding in custom headers */
  inline?: boolean | undefined;
  /** Callback to rename the sheet - shows edit button when provided */
  onRenameSheet?: (() => void) | undefined;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export const SpreadsheetToolbar: React.FC<SpreadsheetToolbarProps> = ({
  sheetName,
  selectedCell,
  currentFormat,
  onFormatChange,
  onAddRow,
  onDeleteRow,
  onAddColumn,
  onDeleteColumn,
  onCopy,
  onPaste,
  liveCellCount,
  onRefreshLive,
  isLoadingLive,
  canDeleteRow,
  canDeleteColumn,
  showHelp = false,
  onToggleHelp,
  readOnly = false,
  inline = false,
  onRenameSheet,
}) => {
  // The toolbar controls content - used in both inline and container modes
  const controls = (
    <div className="flex items-center gap-1 flex-1">
      {/* Row/Column operations */}
      {!readOnly && (
        <>
          <div className="flex items-center gap-0.5">
            <button
              onClick={onAddRow}
              className="p-1 text-slate-400 hover:text-white hover:bg-slate-700 rounded text-[10px] flex items-center gap-0.5"
              title="Add row below"
            >
              <Plus className="w-3 h-3" />
              Row
            </button>
            <button
              onClick={onAddColumn}
              className="p-1 text-slate-400 hover:text-white hover:bg-slate-700 rounded text-[10px] flex items-center gap-0.5"
              title="Add column right"
            >
              <Plus className="w-3 h-3" />
              Col
            </button>
            <button
              onClick={onDeleteRow}
              disabled={!canDeleteRow}
              className="p-1 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded disabled:opacity-30 text-[10px] flex items-center gap-0.5"
              title="Delete row"
            >
              <Trash2 className="w-3 h-3" />
              Row
            </button>
            <button
              onClick={onDeleteColumn}
              disabled={!canDeleteColumn}
              className="p-1 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded disabled:opacity-30 text-[10px] flex items-center gap-0.5"
              title="Delete column"
            >
              <Trash2 className="w-3 h-3" />
              Col
            </button>
          </div>

          <div className="w-px h-4 bg-slate-700 mx-1" />
        </>
      )}

      {/* Alignment */}
      <div className="flex items-center gap-0.5">
        <button
          onClick={() => onFormatChange({ align: "left" })}
          disabled={!selectedCell || readOnly}
          className={`p-1 rounded disabled:opacity-30 ${
            currentFormat?.align === "left"
              ? "bg-slate-600 text-white"
              : "text-slate-400 hover:text-white hover:bg-slate-700"
          }`}
          title="Align left"
        >
          <AlignLeft className="w-3 h-3" />
        </button>
        <button
          onClick={() => onFormatChange({ align: "center" })}
          disabled={!selectedCell || readOnly}
          className={`p-1 rounded disabled:opacity-30 ${
            currentFormat?.align === "center"
              ? "bg-slate-600 text-white"
              : "text-slate-400 hover:text-white hover:bg-slate-700"
          }`}
          title="Align center"
        >
          <AlignCenter className="w-3 h-3" />
        </button>
        <button
          onClick={() => onFormatChange({ align: "right" })}
          disabled={!selectedCell || readOnly}
          className={`p-1 rounded disabled:opacity-30 ${
            currentFormat?.align === "right"
              ? "bg-slate-600 text-white"
              : "text-slate-400 hover:text-white hover:bg-slate-700"
          }`}
          title="Align right"
        >
          <AlignRight className="w-3 h-3" />
        </button>
      </div>

      <div className="w-px h-4 bg-slate-700 mx-1" />

      {/* Number formats */}
      <div className="flex items-center gap-0.5">
        <button
          onClick={() => onFormatChange({ format: "number" })}
          disabled={!selectedCell || readOnly}
          className={`p-1 rounded disabled:opacity-30 ${
            currentFormat?.format === "number"
              ? "bg-slate-600 text-white"
              : "text-slate-400 hover:text-white hover:bg-slate-700"
          }`}
          title="Number format"
        >
          <Hash className="w-3 h-3" />
        </button>
        <button
          onClick={() => onFormatChange({ format: "currency" })}
          disabled={!selectedCell || readOnly}
          className={`p-1 rounded disabled:opacity-30 ${
            currentFormat?.format === "currency"
              ? "bg-slate-600 text-white"
              : "text-slate-400 hover:text-white hover:bg-slate-700"
          }`}
          title="Currency format"
        >
          <DollarSign className="w-3 h-3" />
        </button>
        <button
          onClick={() => onFormatChange({ format: "percent" })}
          disabled={!selectedCell || readOnly}
          className={`p-1 rounded disabled:opacity-30 ${
            currentFormat?.format === "percent"
              ? "bg-slate-600 text-white"
              : "text-slate-400 hover:text-white hover:bg-slate-700"
          }`}
          title="Percent format"
        >
          <Percent className="w-3 h-3" />
        </button>
        <button
          onClick={() => onFormatChange({ format: "compact" })}
          disabled={!selectedCell || readOnly}
          className={`p-1 rounded text-[9px] font-bold disabled:opacity-30 ${
            currentFormat?.format === "compact"
              ? "bg-slate-600 text-white"
              : "text-slate-400 hover:text-white hover:bg-slate-700"
          }`}
          title="Compact format (K, M, B)"
        >
          1K
        </button>
      </div>

      <div className="w-px h-4 bg-slate-700 mx-1" />

      {/* Copy/Paste */}
      <div className="flex items-center gap-0.5">
        <button
          onClick={onCopy}
          disabled={!selectedCell}
          className="p-1 text-slate-400 hover:text-white hover:bg-slate-700 rounded disabled:opacity-30"
          title="Copy (Ctrl+C)"
        >
          <Copy className="w-3 h-3" />
        </button>
        <button
          onClick={onPaste}
          disabled={!selectedCell || readOnly}
          className="p-1 text-slate-400 hover:text-white hover:bg-slate-700 rounded disabled:opacity-30"
          title="Paste (Ctrl+V)"
        >
          <ClipboardPaste className="w-3 h-3" />
        </button>
      </div>

      <div className="flex-1" />

      {/* Live data indicator */}
      {liveCellCount > 0 && (
        <button
          onClick={onRefreshLive}
          disabled={isLoadingLive}
          className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] bg-emerald-500/20 text-emerald-400 rounded hover:bg-emerald-500/30 transition-colors disabled:opacity-50"
          title="Refresh live data"
        >
          {isLoadingLive ? (
            <Loader2 className="w-2.5 h-2.5 animate-spin" />
          ) : (
            <RefreshCw className="w-2.5 h-2.5" />
          )}
          {liveCellCount} live
        </button>
      )}

      {/* Help */}
      {onToggleHelp && (
        <button
          onClick={onToggleHelp}
          className={`p-1 rounded ${
            showHelp
              ? "bg-slate-600 text-white"
              : "text-slate-400 hover:text-white hover:bg-slate-700"
          }`}
          title="Formula help"
        >
          <HelpCircle className="w-3 h-3" />
        </button>
      )}
    </div>
  );

  // Return controls directly when inline mode, otherwise wrap in container
  if (inline) {
    return controls;
  }

  return (
    <div className="flex items-center gap-1 px-2 py-1.5 border-b border-slate-700/50 bg-slate-800/50 flex-shrink-0">
      {/* Title */}
      <span className="text-xs font-medium text-slate-300 truncate max-w-[150px]">
        {sheetName || "Untitled Sheet"}
      </span>
      {onRenameSheet && !readOnly && (
        <button
          onClick={onRenameSheet}
          className="p-0.5 text-slate-500 hover:text-slate-300 rounded"
          title="Rename sheet"
        >
          <Pencil className="w-3 h-3" />
        </button>
      )}

      <div className="w-px h-4 bg-slate-700 mx-1" />

      {controls}
    </div>
  );
};

export default SpreadsheetToolbar;
